/**
 * Thin typed wrappers around the three coupon endpoints
 * (preview / claim / my). Uses the shared `apiClient` so the JWT
 * auth header is attached automatically for the authed endpoints —
 * preview is public so the missing-header case is fine.
 *
 * Error normalization: the backend returns
 *   { error: { code: 'NOT_FOUND', message: '...' } }
 * on every CouponError path. We surface that shape on the rejected
 * promise via a CouponApiError so the page state machine can branch
 * cleanly on `err.code` without poking at axios internals.
 */
import { AxiosError } from 'axios';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../config/api';
import {
  ClaimReceipt,
  ClaimResponse,
  CouponErrorCode,
  CouponPreview,
  MyCouponsResponse,
} from '../types';

/**
 * Structured rejection from any coupon-API call. The page consumes
 * `code` (the stable backend enum) to pick the right user-facing copy.
 * `status` is along for the ride in case future code wants to branch
 * specifically on 5xx vs 4xx.
 */
export class CouponApiError extends Error {
  code: CouponErrorCode | 'NETWORK' | 'UNKNOWN';
  status?: number;
  constructor(
    code: CouponErrorCode | 'NETWORK' | 'UNKNOWN',
    message: string,
    status?: number,
  ) {
    super(message);
    this.name = 'CouponApiError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Convert an axios failure into our structured error type. We DON'T
 * leak axios types upward — every other layer in this feature works
 * exclusively against CouponApiError so the unit tests don't need to
 * mock the full axios surface.
 */
function toCouponApiError(err: unknown): CouponApiError {
  const ax = err as AxiosError<{ error?: { code?: string; message?: string } }>;
  // No response object at all = network reached out and nothing came back.
  if (!ax?.response) {
    return new CouponApiError(
      'NETWORK',
      ax?.message || 'Network error',
    );
  }
  const body = ax.response.data;
  const code = (body?.error?.code as CouponErrorCode | undefined) || 'UNKNOWN';
  const message = body?.error?.message || ax.message || 'Request failed';
  return new CouponApiError(code, message, ax.response.status);
}

/**
 * Public — no auth needed. Backend rate-limits to 5/min/IP. Returns
 * the reward shape so the page can render <RewardSummary> before the
 * user commits to claiming.
 */
export async function previewCode(code: string): Promise<CouponPreview> {
  try {
    const { data } = await apiClient.post<CouponPreview>(
      API_ENDPOINTS.COUPON_PREVIEW,
      { code },
    );
    return data;
  } catch (err) {
    throw toCouponApiError(err);
  }
}

/**
 * Authed — requires a valid JWT (apiClient attaches it automatically
 * from localStorage). Backend rate-limits to 3/min/user.
 *
 * Returns the receipt that the success screen renders. A repeat claim
 * by the same user for the same code is idempotent — the receipt comes
 * back with `idempotent:true` and no new rewards are applied.
 */
export async function claimCode(code: string): Promise<ClaimReceipt> {
  try {
    const { data } = await apiClient.post<ClaimResponse>(
      API_ENDPOINTS.COUPON_CLAIM,
      { code },
    );
    return data.receipt;
  } catch (err) {
    throw toCouponApiError(err);
  }
}

/**
 * Authed — list this user's claimed + pending coupon rewards plus their
 * current free-access window. Not used by the redeem page directly,
 * but exported here so a profile or dashboard surface can adopt the
 * same typed wrapper.
 */
export async function getMyCoupons(): Promise<MyCouponsResponse> {
  try {
    const { data } = await apiClient.get<MyCouponsResponse>(
      API_ENDPOINTS.COUPON_MY,
    );
    return data;
  } catch (err) {
    throw toCouponApiError(err);
  }
}
